import { useEffect, useState } from "react";
import { api, money } from "@/hub/lib/api";
import { Leaf, Check, CalendarCheck, Loader2 } from "lucide-react";

export default function PublicBooking() {
  const [zonesAvail, setZonesAvail] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activities, setActivities] = useState([]);
  const [f, setF] = useState({ client_name: "", email: "", phone: "", type: "Leisure", start_date: "", end_date: "", zone_id: "", package_id: "", pax: "" , marketing_consent: false });
  const [acts, setActs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const [cal, setCal] = useState({ month: new Date().toISOString().slice(0, 7), data: null });
  useEffect(() => { api.publicCalendar(cal.month).then((d) => setCal((x) => ({ ...x, data: d }))); }, [cal.month]);
  useEffect(() => {
    api.packages().then((ps) => {
      setPackages(ps);
      const qp = new URLSearchParams(window.location.search).get("package");
      const hit = qp && ps.find((x) => x.id === qp);
      if (hit) setF((x) => ({ ...x, package_id: qp, type: hit.type === "Training" ? "Training" : "Leisure" }));
    });
    api.activities().then(setActivities);
  }, []);
  const shiftMonth = (n) => setCal((x) => { const [y, m] = x.month.split("-").map(Number); return { ...x, month: new Date(y, m - 1 + n, 1).toISOString().slice(0, 7) }; });
  const calLabel = new Date(cal.month + "-02").toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const firstDow = (new Date(cal.month + "-01").getDay() + 6) % 7;
  const calCells = [...Array(firstDow).fill(null), ...(cal.data?.days || []).map((d) => ({ ...d, day: Number(d.date.slice(8)) }))];
  const pickDay = (dateStr) => setF((x) => {
    if (!x.start_date || (x.start_date && x.end_date)) return { ...x, start_date: dateStr, end_date: "" };
    return dateStr >= x.start_date ? { ...x, end_date: dateStr } : { ...x, start_date: dateStr, end_date: x.start_date };
  });
  useEffect(() => {
    if (f.start_date && f.end_date && f.end_date >= f.start_date) api.availabilityRange(f.start_date, f.end_date).then(setZonesAvail);
    else setZonesAvail([]);
  }, [f.start_date, f.end_date]);

  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const outdoor = zonesAvail.filter((a) => a.zone.type === "outdoor");
  const selZone = zonesAvail.find((a) => a.zone.id === f.zone_id)?.zone;
  const pkgList = packages.filter((p) => (f.type === "Training" ? p.type === "Training" : p.type !== "Training"));
  const selPkg = packages.find((p) => p.id === f.package_id);
  const isCustom = f.package_id === "__custom__";
  const includedIds = new Set((selPkg?.items || []).map((i) => i.activity_id));
  const toggleAct = (a) => setActs((l) => (l.find((x) => x.activity_id === a.id) ? l.filter((x) => x.activity_id !== a.id) : [...l, { activity_id: a.id, name: a.name, price: a.cost, qty: 1 }]));
  const total = (selPkg?.base_price || 0) + acts.reduce((s, a) => s + a.price, 0);

  const submit = async () => {
    setErr("");
    if (!f.client_name.trim()) return setErr("Please enter your name.");
    if (!f.start_date || !f.end_date) return setErr("Please choose your dates.");
    if (!f.zone_id) return setErr("Please select an available location.");
    setBusy(true);
    try {
      await api.publicBooking({ ...f, package_id: isCustom ? null : (f.package_id || null), custom_package: isCustom, pax: Number(f.pax) || 0, additional_activities: acts, meals: [] });
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "This date and location may already be booked. Please pick another.");
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="min-h-screen bg-[hsl(60,20%,97%)] flex items-center justify-center p-5">
      <div className="bg-white border border-stone-200 rounded-sm p-8 max-w-md w-full text-center">
        <div className="h-14 w-14 rounded-full bg-forest/10 flex items-center justify-center mx-auto mb-4"><CalendarCheck className="h-7 w-7 text-forest" /></div>
        <h1 className="font-heading text-2xl font-semibold text-forest">Booking Requested!</h1>
        <p className="text-sm text-stone-500 mt-2">Thank you, {f.client_name}. Your reservation for {f.start_date === f.end_date ? f.start_date : `${f.start_date} → ${f.end_date}`} is confirmed. Our team will reach out shortly.</p>
        <div className="mt-5 text-left border-t border-stone-100 pt-4 space-y-3" data-testid="pb-summary">
          <Recap label="Activities">
            {acts.length === 0 ? <span className="text-xs text-stone-400">None</span> : (
              <div className="flex flex-wrap gap-1.5">{acts.map((a) => <span key={a.activity_id} className="text-[11px] bg-terracotta/10 text-terracotta px-1.5 py-0.5 rounded-sm">{a.name}</span>)}</div>
            )}
          </Recap>
          <Recap label="Facilities">{selZone?.name || "—"}</Recap>
          <Recap label="Accommodation">None</Recap>
          <Recap label="Meal Menu">None</Recap>
          <Recap label="Other">{isCustom ? "Customized Package" : (selPkg?.name || "No package")} · {f.pax || 0} guests</Recap>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[hsl(60,20%,97%)] py-6 px-4" style={{ fontFamily: "'IBM Plex Sans',sans-serif" }}>
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-2 mb-5">
          <div className="h-9 w-9 bg-forest flex items-center justify-center rounded-sm"><Leaf className="h-5 w-5 text-white" /></div>
          <div><div className="font-heading font-bold text-forest leading-tight">LUSH</div><div className="text-[10px] uppercase tracking-[0.2em] text-stone-400">Book your escape</div></div>
        </div>

        <div className="bg-white border border-stone-200 rounded-sm p-5 mb-4" data-testid="pb-calendar">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] uppercase tracking-wider text-stone-400">Availability Calendar</span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => shiftMonth(-1)} className="px-2 py-1 border border-stone-200 rounded-sm text-xs" data-testid="pb-cal-prev">&lsaquo;</button>
              <span className="text-sm font-medium min-w-[110px] text-center">{calLabel}</span>
              <button type="button" onClick={() => shiftMonth(1)} className="px-2 py-1 border border-stone-200 rounded-sm text-xs" data-testid="pb-cal-next">&rsaquo;</button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-stone-400 mb-1">
            {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <span key={d}>{d}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {calCells.map((c, i) => (c ? (
              <button key={i} type="button" onClick={() => pickDay(c.date)} data-testid={`pb-day-${c.date}`}
                className={`relative aspect-square rounded-sm border text-xs flex items-center justify-center transition-colors ${(f.start_date === c.date || f.end_date === c.date) ? "border-forest bg-forest text-white" : c.free === 0 ? "border-red-200 bg-red-50 text-red-400" : c.booked_count > 0 ? "border-orange-200 bg-orange-50" : "border-stone-200 hover:border-forest"}`}>
                {c.day}
                <span className={`absolute bottom-1 h-1 w-1 rounded-full ${c.free === 0 ? "bg-red-400" : c.booked_count > 0 ? "bg-orange-400" : "bg-emerald-500"}`} />
              </button>
            ) : <span key={i} />))}
          </div>
          <div className="flex flex-wrap gap-4 mt-3 text-[10px] text-stone-400">
            <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Open</span>
            <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-orange-400" /> Partially booked</span>
            <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-red-400" /> Fully booked</span>
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-sm p-5 space-y-4">
          <Field label="Your Name"><input value={f.client_name} onChange={set("client_name")} className="inp" data-testid="pb-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email"><input value={f.email} onChange={set("email")} className="inp" data-testid="pb-email" /></Field>
            <Field label="Phone"><input value={f.phone} onChange={set("phone")} className="inp" data-testid="pb-phone" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From"><input type="date" value={f.start_date} onChange={set("start_date")} className="inp" data-testid="pb-from" /></Field>
            <Field label="To"><input type="date" value={f.end_date} min={f.start_date} onChange={set("end_date")} className="inp" data-testid="pb-to" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Experience">
              <select value={f.type} onChange={set("type")} className="inp" data-testid="pb-type"><option>Leisure</option><option>Training</option></select>
            </Field>
            <Field label="Guests"><input type="number" value={f.pax} onChange={set("pax")} className="inp" data-testid="pb-pax" /></Field>
          </div>

          <Field label="Choose a Location">
            {zonesAvail.length === 0 ? <p className="text-xs text-stone-400">Pick your dates to see availability.</p> : (
              <div className="grid grid-cols-1 gap-2" data-testid="pb-zones">
                {outdoor.map((a) => (
                  <button key={a.zone.id} type="button" disabled={!a.available} onClick={() => setF((x) => ({ ...x, zone_id: a.zone.id }))}
                    data-testid={`pb-zone-${a.zone.id}`}
                    className={`flex items-center justify-between p-3 rounded-sm border text-left transition-colors ${!a.available ? "opacity-50 cursor-not-allowed bg-stone-50 border-stone-200" : f.zone_id === a.zone.id ? "border-forest bg-forest/5 ring-1 ring-forest" : "border-stone-200 hover:border-forest"}`}>
                    <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm" style={{ background: a.zone.color }} /><span className="text-sm font-medium">{a.zone.name}</span></span>
                    {a.available ? <span className="text-xs text-emerald-600 font-semibold">Available</span> : <span className="text-xs text-red-500 font-semibold">Booked ({a.reserved_by})</span>}
                  </button>
                ))}
              </div>
            )}
          </Field>

          <Field label="Package">
            <select value={f.package_id} onChange={set("package_id")} className="inp" data-testid="pb-package">
              <option value="">No package</option>
              {pkgList.map((p) => <option key={p.id} value={p.id}>{p.name} · {money(p.base_price)}</option>)}
              <option value="__custom__">Customized Package (build your own)</option>
            </select>
          </Field>

          <Field label="Add Activities">
            <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-auto" data-testid="pb-activities">
              {activities.map((a) => {
                const included = includedIds.has(a.id);
                const on = acts.find((x) => x.activity_id === a.id);
                return <button key={a.id} type="button" disabled={included} onClick={() => !included && toggleAct(a)} className={`text-left text-xs p-2 rounded-sm border ${included ? "opacity-50 bg-stone-50 border-stone-200 cursor-not-allowed" : on ? "border-terracotta bg-terracotta/5" : "border-stone-200"}`}>{included ? <Check className="h-3 w-3 inline mr-1 text-forest" /> : (on && <Check className="h-3 w-3 inline mr-1 text-terracotta" />)}{a.name} <span className="text-stone-400">{included ? "· in package" : money(a.cost)}</span></button>;
              })}
            </div>
          </Field>

          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm p-2" data-testid="pb-error">{err}</div>}

          <div className="flex items-center justify-between pt-1">
            <span className="text-sm text-stone-500">Est. total</span>
            <span className="font-mono text-lg text-forest">{money(total)}</span>
          </div>
          <button onClick={submit} disabled={busy} className="w-full bg-forest text-white rounded-sm py-3 font-medium hover:bg-[#142F27] transition-colors flex items-center justify-center gap-2 disabled:opacity-60" data-testid="pb-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Request Booking
          </button>
          <p className="text-[11px] text-center text-stone-400">You'll get a confirmation and our team will finalise your reservation.</p>
        </div>
      </div>
      <style>{`.inp{width:100%;border:1px solid #e7e5e4;border-radius:2px;padding:8px 10px;font-size:14px;background:#fff;outline:none}.inp:focus{border-color:#1C3F35}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label className="text-[10px] uppercase tracking-wider text-stone-400 block mb-1">{label}</label>{children}</div>
);

const Recap = ({ label, children }) => (
  <div className="flex items-start gap-3">
    <span className="text-[10px] uppercase tracking-[0.15em] text-stone-400 min-w-[90px] mt-0.5">{label}</span>
    <div className="text-xs text-stone-600 flex-1">{children}</div>
  </div>
);
